/**
 * @graphql cc
 */
import React, { useCallback, Fragment } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { DatabasePrerequisites } from '@confluence/content-prerequisites/entry-points/DatabasePrerequisites.ts';
import { MovePageSuccessFlag } from '@confluence/page-tree/entry-points/MovePageSuccessFlag.tsx';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { DATABASE_VIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { usePubSubEvent } from '@confluence/pubsub-client/entry-points/usePubSubEvent.ts';
import { CommentProvider } from '@confluence/content-types-bridge-comments';
import { expVal } from '@confluence/feature-experiments';
import { useContentRestrictionSubscriptionEvent } from '@confluence/editor-features/entry-points/useContentRestrictionSubscriptionEvent.ts';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';
import { fg } from '@confluence/feature-gating';

import { DatabaseShell } from './DatabaseShell.tsx';
import {
	type DatabaseContainerQuery$data as DatabaseContainerQueryType,
	type DatabaseContainerQuery$variables as DatabaseContainerQueryVariables,
} from './__generated__/DatabaseContainerQuery.graphql.ts';

// Utility type to make all properties of T mutable (non-readonly)
type Mutable<T> = {
	-readonly [P in keyof T]: Mutable<T[P]>;
};

type DatabaseContainerType = {
	contentId: string;
	spaceKey: string;
};

import { graphql } from 'react-relay';

export const DatabaseContainerQuery = graphql`
	query DatabaseContainerQuery($contentId: ID!, $spaceKey: String, $contentVersion: Int!) {
		content(id: $contentId) {
			nodes {
				id
				referenceId
				title
				operations {
					operation
					targetType
				}
				hasRestrictions
				hasViewRestrictions
				hasInheritedRestrictions
				space {
					id
					name
					key
					type
					alias
				}
				type
				status
				ancestors {
					id
					type
					title
					status
				}
			}
		}
		spaces(spaceKey: $spaceKey, first: 10) {
			nodes {
				id
				name
				homepage {
					id
				}
			}
		}
		user: confluence_atlassianUser(current: true) {
			id
			displayName
			photos {
				value
				isPrimary
			}
			emails {
				value
				isPrimary
			}
			locale
			confluence {
				space {
					id
				}
			}
		}

		dataSecurityPolicy {
			isActionEnabledForContent(
				action: PAGE_EXPORT
				contentId: $contentId
				contentStatus: CURRENT
				spaceKey: $spaceKey
				contentVersion: $contentVersion
			) {
				allowed
			}
		}
	}
`;

const DatabaseContainerComponent = ({ contentId, spaceKey }: DatabaseContainerType) => {
	const { networkStatus, refetch, ...databaseContainerQueryResult } = useQueryInterop<
		DatabaseContainerQueryType,
		DatabaseContainerQueryVariables
	>(DatabaseContainerQuery, {
		variables: {
			contentId,
			spaceKey,
			// The DSP query doesn't currently user the content version value, so using a fixed value
			contentVersion: 1,
		},
		notifyOnNetworkStatusChange: true,
		context: { single: true },
	});

	const { data, loading } = databaseContainerQueryResult;

	const content = data?.content?.nodes || [];
	const contentReferenceId = content[0]?.referenceId;
	const spaceId = content[0]?.space?.id;
	const spaceName = content[0]?.space?.name;
	const title = content[0]?.title || '';
	const identity = {
		accountId: data?.user?.id,
		nickname: data?.user?.displayName,
		picture: data?.user?.photos?.find((photo) => photo?.isPrimary)?.value,
		email: data?.user?.emails?.find((email) => email?.isPrimary)?.value,
		locale: data?.user?.locale,
	};

	const onEventHandler = useCallback(() => {
		void refetch();
	}, [refetch]);

	const onRestrictionUpdatedPubSubEvent = useCallback(() => {
		if (
			!['DUAL', 'GRAPHQL'].includes(
				StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
			) ||
			!expVal('cc_fps_migration_content-restriction-updated', 'isEnabled', false)
		) {
			onEventHandler();
		}
	}, [onEventHandler]);

	usePubSubEvent({
		contentId,
		contentType: 'database',
		eventName: `restrictionUpdated:database`,
		attribution: Attribution.CONTENT_TYPES,
		onEvent: onRestrictionUpdatedPubSubEvent,
		skip:
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			expVal('cc_fps_migration_content-restriction-updated', 'isEnabled', false) &&
			fg('confluence_enable_subscriptions-context-provider') &&
			StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', '') ===
				'GRAPHQL',
	});

	useContentRestrictionSubscriptionEvent({
		contentId,
		contentType: 'database',
		callback: onEventHandler,
		subscriberId: 'DatabaseContainerSubscription',
	});

	const pageExport = data?.dataSecurityPolicy?.isActionEnabledForContent?.allowed;
	const dataSecurityPolicyPageExport = pageExport != undefined ? pageExport : null;

	const {
		data: databaseContainerQueryResultData,
		error: databaseContainerQueryResultError,
		loading: databaseContainerQueryResultLoading,
	} = databaseContainerQueryResult;

	return (
		<Fragment>
			<DatabasePrerequisites
				route={DATABASE_VIEW}
				contentQueryResult={{
					data: databaseContainerQueryResultData as Mutable<DatabaseContainerQueryType> | undefined,
					error: databaseContainerQueryResultError,
					loading: databaseContainerQueryResultLoading,
				}}
			>
				<CommentProvider>
					{expVal('confluence_modernized_move_dialog', 'isEnabled', false) && (
						<MovePageSuccessFlag />
					)}
					<DatabaseShell
						spaceKey={spaceKey}
						contentId={contentId}
						contentReferenceId={contentReferenceId}
						loading={loading}
						spaceId={spaceId}
						queryNetworkStatus={networkStatus}
						spaceName={spaceName}
						title={title}
						identity={identity}
						refetch={refetch}
						dataSecurityPolicyPageExport={dataSecurityPolicyPageExport}
					/>
				</CommentProvider>
			</DatabasePrerequisites>
		</Fragment>
	);
};

export const DatabaseContainer = DatabaseContainerComponent;
